import { Box } from '@mui/material';
import { useCallback } from 'react';
import { Typography, colors } from '@sweep-io/sweep-design';
import { TeamsMessageEditor } from '../slack/TeamsMessageEditor';
import { useRunOnce } from '../../common/useRunOnce';
import { SlackAdditionalFieldsTable } from '../slack/SlackAdditionalFieldsTable';
import {
  selectTeamsIntegrationStatus,
  TeamsConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { TeamsNotConnected } from './TeamsNotConnected';
import { useSelector } from 'react-redux';
import { TeamsSelectDealroomType } from './TeamsSelectDealroomType';
import { TeamsRecipientType } from '../teams/types';

interface TeamsArchiveDealRoomActionProps {
  readonly?: boolean;
  action: TeamsArchiveDealroomAutomationAction;
  crmOrgId: string;
  onChange: (teamsArchiveDealroomAutomationAction: TeamsArchiveDealroomAutomationAction) => any;
  objectName: string;
}

export const TeamsArchiveDealroomAction = ({
  readonly,
  action,
  crmOrgId,
  objectName,
  onChange,
}: TeamsArchiveDealRoomActionProps) => {
  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  readonly = readonly || teamsStatus !== TeamsConnectionStatus.Connected;

  useRunOnce(async () => {
    updateOnChange({
      fields: [...(action.actionParams?.fields || [])],
      channelRef: action.actionParams?.channelRef
        ? { ...action.actionParams?.channelRef }
        : {
            type: TeamsRecipientType.CHANNEL_STORED_IN_SWEEP,
          },
    });
  });

  const updateOnChange = useCallback(
    (updateProps: Partial<AutomationTeamsRenameDealroomActionParams>) => {
      onChange({
        ...action,
        actionParams: {
          ...action.actionParams,
          ...updateProps,
        },
      });
    },
    [action, onChange],
  );

  return (
    <>
      {teamsStatus !== TeamsConnectionStatus.Connected && (
        <Box sx={{ marginTop: '32px' }}>
          <TeamsNotConnected teamsStatus={teamsStatus} />
        </Box>
      )}

      {(teamsStatus === TeamsConnectionStatus.Connected || action?.actionParams?.message) && (
        <Box
          sx={{
            background: colors.grey[100],
            padding: '24px 24px 19px',
            borderRadius: '8px',
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TeamsSelectDealroomType
            readonly={readonly}
            value={action?.actionParams?.channelRef}
            crmOrgId={crmOrgId}
            objectName={objectName}
            onChange={(
              val: SweepCreatedTeamsChannel | SalesforceCreatedTeamsChannel | undefined,
            ) => {
              updateOnChange({ channelRef: val });
            }}
          />

          <TeamsMessageEditor
            placeholder="Type message body..."
            isRequired={false}
            label="Message (optional)"
            readonly={readonly}
            messageStruct={action.actionParams?.message ?? { template: '', variables: [] }}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({ message: newMsg });
            }}
            objectName={objectName}
          />

          <SlackAdditionalFieldsTable
            readonly={readonly}
            fields={action.actionParams?.fields || []}
            updateOnChange={(fields) => {
              updateOnChange({ fields });
            }}
            crmOrgId={crmOrgId}
            objectName={objectName}
          />
          <Typography variant="body">
            Note: this automation would work as expected only if the record has a dedicated deal
            room
          </Typography>
        </Box>
      )}
    </>
  );
};
